/**
 * Canonical section-total assembly for Haul & Dump + per-row markup.
 *
 * SINGLE SOURCE OF TRUTH. The estimate builder (bid-totals-panel.tsx) authors
 * `estimate.total` and the saved `markupData` array using exactly this row model.
 * Every render surface — the builder, the on-screen proposal preview, the
 * client-facing signature page, and the backend PDF service — MUST fold H&D and
 * markup into section totals through this module so their numbers agree.
 *
 * Why this exists: H&D lives in a separate `lchData` blob, not as line items.
 * Historically each surface re-implemented the "bucket by section + inject H&D +
 * apply markup" logic with its own grouping, and they drifted — most sharply on
 * the H&D markup index, which shifted whenever an asphalt section had items in
 * more than one sub-section (asphalt-rnr + asphalt-overlay) sharing a category.
 * See brain/Decisions/2026-07-07-hd-markup-index-divergence.md.
 *
 * The canonical grouping (mirrored from the builder's buildSectionRows):
 *   - `asphalt`, `asphalt-rnr`, `asphalt-overlay` MERGE into one "Asphalt" prefix,
 *     grouped by category; `asphalt-grading` is a separate "Asphalt/Grading" prefix.
 *   - `concrete` + `concrete-grading` merge similarly.
 *   - Rows are ordered [...group rows..., Haul & Dump (if any), Totals].
 *   - `markupData[sectionKey][i]` maps positionally to row i (the Totals row is
 *     always the trailing 0% entry).
 */

export interface CostRow {
  category: string;
  subTotal: number;
  markupPercent: number;
  markupAmount: number;
  cost: number;
  isTotal?: boolean;
}

export interface SectionLineItem {
  section?: string | null;
  category?: string | null;
  lineTotal: number;
}

export interface SectionLchRow {
  section?: string | null;
  totalHaulDumpCost?: number;
}

export type MarkupData = Record<string, { markupPercent: number }[]>;

/** The five top-level proposal sections, in display order. */
export const PROJECT_SECTIONS = [
  { key: 'asphalt', label: 'Asphalt' },
  { key: 'concrete', label: 'Concrete' },
  { key: 'grading', label: 'Grading' },
  { key: 'seal-coat', label: 'Seal Coat' },
  { key: 'striping', label: 'Striping' },
] as const;

const SECTION_LABELS: Record<string, string> = {
  asphalt: 'Asphalt',
  'asphalt-grading': 'Asphalt/Grading',
  concrete: 'Concrete',
  'concrete-grading': 'Concrete/Grading',
  grading: 'Grading',
  'seal-coat': 'Seal Coat',
  striping: 'Striping',
};

const CATEGORY_LABELS: Record<string, string> = {
  materials: 'Materials',
  equipment: 'Equipment',
  labor: 'Labor',
  misc: 'Miscellaneous',
  crew: 'Crew',
  striping: 'Striping',
};

/** Prisma returns `lchData` as an already-parsed array; strings come from other paths. */
export function parseLchData(raw: unknown): SectionLchRow[] {
  if (Array.isArray(raw)) return raw as SectionLchRow[];
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

/** Sum H&D cost per raw section key (matches the builder's bucketing). */
export function lchCostBySection(lchRows: SectionLchRow[]): Record<string, number> {
  const map: Record<string, number> = {};
  for (const row of lchRows) {
    const sec = (row.section as string) || '_general';
    map[sec] = (map[sec] || 0) + (row.totalHaulDumpCost || 0);
  }
  return map;
}

/**
 * Build the ordered cost rows for one top-level section from all line items.
 * Faithful extraction of buildSectionRows in bid-totals-panel.tsx — keep in sync.
 */
export function buildSectionRows(items: SectionLineItem[], section: string): CostRow[] {
  const label = SECTION_LABELS[section] || section;

  const groupEntries: { prefix: string; cat: string; total: number }[] = [];
  const seen = new Map<string, number>();

  const addItems = (filteredItems: SectionLineItem[], prefix: string) => {
    filteredItems.forEach((li) => {
      const cat = li.category || 'misc';
      const key = `${prefix}|${cat}`;
      const idx = seen.get(key);
      if (idx !== undefined) {
        groupEntries[idx].total += li.lineTotal;
      } else {
        seen.set(key, groupEntries.length);
        groupEntries.push({ prefix, cat, total: li.lineTotal });
      }
    });
  };

  addItems(items.filter((li) => (li.section || null) === section), label);
  if (section === 'asphalt') {
    addItems(items.filter((li) => li.section === 'asphalt-grading'), 'Asphalt/Grading');
    // R&R and Overlay items roll up into the parent "Asphalt" prefix so all
    // asphalt-family items aggregate under a single set of Asphalt lines.
    addItems(items.filter((li) => li.section === 'asphalt-rnr'), label);
    addItems(items.filter((li) => li.section === 'asphalt-overlay'), label);
  }
  if (section === 'concrete') {
    addItems(items.filter((li) => li.section === 'concrete-grading'), 'Concrete/Grading');
  }

  if (groupEntries.length === 0) return [];

  const rows: CostRow[] = groupEntries.map(({ prefix, cat, total }) => ({
    category: `${prefix} ${CATEGORY_LABELS[cat] || cat}`,
    subTotal: total,
    markupPercent: 0,
    markupAmount: 0,
    cost: total,
  }));

  const sectionSubTotal = rows.reduce((s, r) => s + r.subTotal, 0);
  rows.push({
    category: `${label} Totals`,
    subTotal: sectionSubTotal,
    markupPercent: 0,
    markupAmount: 0,
    cost: sectionSubTotal,
    isTotal: true,
  });

  return rows;
}

/**
 * Build the section rows with the Haul & Dump and Subcontractors rows injected
 * before the Totals row. The Bid Totals panel builds its rows through this too.
 * Returns [] when the section has no line items, H&D or subcontractors.
 *
 * Row order: [...line-item categories..., Haul & Dump, Subcontractors, Totals].
 * Subcontractors go LAST, directly before Totals, because `markupData` maps
 * POSITIONALLY: appending after everything that already existed means every row
 * a bid had before per-service subcontractors keeps its index, so no saved
 * markup % can shift onto a different row.
 */
export function buildSectionRowsWithHD(
  items: SectionLineItem[],
  section: string,
  hdCost: number,
  subcontractorCost = 0,
): CostRow[] {
  const rows = buildSectionRows(items, section);
  const label = SECTION_LABELS[section] || section;

  const extras: CostRow[] = [];
  if (hdCost && hdCost > 0) {
    extras.push({
      category: `${label} Haul & Dump`,
      subTotal: hdCost,
      markupPercent: 0,
      markupAmount: 0,
      cost: hdCost,
    });
  }
  if (subcontractorCost && subcontractorCost > 0) {
    extras.push({
      category: `${label} Subcontractors`,
      subTotal: subcontractorCost,
      markupPercent: 0,
      markupAmount: 0,
      cost: subcontractorCost,
    });
  }
  if (extras.length === 0) return rows;

  const totalRow = rows[rows.length - 1];
  if (totalRow?.isTotal) {
    rows.splice(rows.length - 1, 0, ...extras);
    const nonTotalRows = rows.filter((r) => !r.isTotal);
    const sectionSubTotal = nonTotalRows.reduce((s, r) => s + r.subTotal, 0);
    totalRow.subTotal = sectionSubTotal;
    totalRow.cost = sectionSubTotal;
  } else {
    // No line items in this section — only H&D and/or subcontractors.
    rows.push(...extras);
    const sectionSubTotal = extras.reduce((s, r) => s + r.subTotal, 0);
    rows.push({
      category: `${label} Totals`,
      subTotal: sectionSubTotal,
      markupPercent: 0,
      markupAmount: 0,
      cost: sectionSubTotal,
      isTotal: true,
    });
  }
  return rows;
}

export interface SectionFinancials {
  /** Line-item cost + H&D cost + subcontractor cost, before markup. */
  subtotal: number;
  /** H&D cost portion of the subtotal. */
  hdCost: number;
  /** Per-service subcontractor portion of the subtotal (cost + bonds). */
  subcontractorCost: number;
  /** Total markup applied across all non-total rows (includes H&D's and subcontractors'). */
  markupAmount: number;
  /** subtotal + markupAmount — the section's categoryTotal on the proposal. */
  total: number;
  rows: CostRow[];
}

/**
 * Compute one section's financials by replaying the builder's rows and applying
 * `markupData[sectionKey]` positionally. This is the function every render surface
 * should call — it guarantees the per-section categoryTotal (and its sum) matches
 * the saved `estimate.total`.
 *
 * Pass `subcontractorsBySection` (from subcontractorCostBySection) on EVERY
 * surface. Omitting it silently drops that service's subcontractors from its
 * category total while the grand total still counts them.
 */
export function computeSectionFinancials(
  sectionKey: string,
  allItems: SectionLineItem[],
  lchBySection: Record<string, number>,
  markupData: MarkupData | null | undefined,
  subcontractorsBySection: Record<string, number> = {},
): SectionFinancials {
  const hdCost = lchBySection[sectionKey] || 0;
  const subcontractorCost = subcontractorsBySection[sectionKey] || 0;
  const rows = buildSectionRowsWithHD(allItems, sectionKey, hdCost, subcontractorCost);
  if (rows.length === 0) {
    return { subtotal: 0, hdCost: 0, subcontractorCost: 0, markupAmount: 0, total: 0, rows };
  }
  const md = markupData?.[sectionKey] || [];
  let subtotal = 0;
  let markupAmount = 0;
  rows.forEach((r, i) => {
    if (r.isTotal) return;
    subtotal += r.subTotal;
    const mp = md[i]?.markupPercent ?? 0;
    markupAmount += r.subTotal * (mp / 100);
  });
  return { subtotal, hdCost, subcontractorCost, markupAmount, total: subtotal + markupAmount, rows };
}

/* ────────────────────────────────────────────────────────────────────────────
 * GRAND TOTAL — the bottom-line figure.
 *
 * computeSectionFinancials (above) makes every render surface agree on each
 * CATEGORY total. This section extends that guarantee to the GRAND total, which
 * was historically a separate stored scalar (`estimate.total`) authored only by
 * the Bid Totals panel. When a bid's scope grew without the panel re-saving, the
 * categories recalculated but the scalar stayed frozen, so the proposal printed a
 * "Final Base Bid Total" that no longer equaled the sum of the categories above it
 * (NPG PROP-2026-292: categories summed to $6,499.78, scalar stuck at $3,850).
 * See brain/Decisions/2026-07-08-grand-total-drift.md.
 *
 * computeEstimateFinancials mirrors the Bid Totals panel's `finalTotal` exactly, so
 * the grand total is always DERIVED from the same line items + markup + H&D +
 * subcontractors + discount — never a frozen number.
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * A subcontractor entry. There are two shapes, told apart by `section`:
 *
 *  - SERVICE entries (current): entered inside a service tab and tagged with that
 *    service's key. They become that service's "Subcontractors" row — cost + bonds,
 *    marked up by the row's markup % in markupData like any other category.
 *
 *  - LEGACY entries: from the old standalone Subcontractors tab. No `section`; they
 *    carry their own `markupPercent` and are summed OUTSIDE every section, exactly as
 *    before, so an existing or sent proposal never changes by a cent.
 */
export interface SubcontractorCostRow {
  cost?: number;
  markupPercent?: number;
  bonds?: number;
  section?: string | null;
}

/** Parse the `subcontractorsData` blob (array from Prisma, string from other paths). */
export function parseSubcontractors(raw: unknown): SubcontractorCostRow[] {
  if (Array.isArray(raw)) return raw as SubcontractorCostRow[];
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

const PROJECT_SECTION_KEYS = new Set<string>(PROJECT_SECTIONS.map((s) => s.key));

/**
 * A service entry is one tagged with one of the five project sections. Anything
 * else — no `section`, or an unrecognised one — is treated as legacy, so it is
 * still counted (outside the sections) rather than silently vanishing.
 */
export function isServiceSubcontractor(r: SubcontractorCostRow): boolean {
  return !!r?.section && PROJECT_SECTION_KEYS.has(r.section);
}

/**
 * Per-service subcontractor cost by section key: cost + bonds, before markup.
 * Markup comes from that section's Subcontractors row in markupData.
 */
export function subcontractorCostBySection(rows: SubcontractorCostRow[]): Record<string, number> {
  const map: Record<string, number> = {};
  for (const r of rows) {
    if (!isServiceSubcontractor(r)) continue;
    const sec = r.section as string;
    map[sec] = (map[sec] || 0) + (r?.cost || 0) + (r?.bonds || 0);
  }
  return map;
}

/**
 * Legacy (unassigned) entries only: cost + their own markup + bonds, summed outside
 * every section — unchanged from before per-service subcontractors existed.
 */
export function sumLegacySubcontractors(rows: SubcontractorCostRow[]): number {
  return rows.reduce((sum, r) => {
    if (isServiceSubcontractor(r)) return sum;
    const cost = r?.cost || 0;
    const markup = cost * ((r?.markupPercent || 0) / 100);
    const bonds = r?.bonds || 0;
    return sum + cost + markup + bonds;
  }, 0);
}

/**
 * Build ordered cost rows for legacy line items that have NO section, grouped by
 * category. Mirrors buildUnsectionedRows in bid-totals-panel.tsx; `markupData['_general']`
 * maps positionally to these rows.
 */
export function buildUnsectionedRows(items: SectionLineItem[]): CostRow[] {
  const unsectioned = items.filter((li) => !li.section);
  if (unsectioned.length === 0) return [];

  const groupEntries: { cat: string; total: number }[] = [];
  const seen = new Map<string, number>();
  unsectioned.forEach((li) => {
    const cat = li.category || 'misc';
    const idx = seen.get(cat);
    if (idx !== undefined) {
      groupEntries[idx].total += li.lineTotal;
    } else {
      seen.set(cat, groupEntries.length);
      groupEntries.push({ cat, total: li.lineTotal });
    }
  });

  const rows: CostRow[] = groupEntries.map(({ cat, total }) => ({
    category: CATEGORY_LABELS[cat] || cat,
    subTotal: total,
    markupPercent: 0,
    markupAmount: 0,
    cost: total,
  }));

  const subTotal = rows.reduce((s, r) => s + r.subTotal, 0);
  rows.push({
    category: 'General Totals',
    subTotal,
    markupPercent: 0,
    markupAmount: 0,
    cost: subTotal,
    isTotal: true,
  });
  return rows;
}

export interface EstimateFinancialsInput {
  lineItems: SectionLineItem[];
  lchRows: SectionLchRow[];
  markupData: MarkupData | null | undefined;
  subcontractors?: SubcontractorCostRow[] | null;
  discountType?: string | null;
  discountValue?: number | null;
}

export interface EstimateFinancials {
  /**
   * All line items + all H&D + all subcontractors, before per-row markup. Service
   * subcontractors count at cost + bonds (their markup is per-row, below); legacy
   * ones count at cost + their own markup + bonds.
   */
  costSubtotal: number;
  /** Per-row markup summed across every section (project sections + legacy unsectioned). */
  markupAmount: number;
  /** costSubtotal + markupAmount — the sum of every category total shown, before discount. */
  grandTotalBeforeDiscount: number;
  discountAmount: number;
  /** The bid's final total — matches the Bid Totals panel's finalTotal exactly. */
  total: number;
}

/**
 * Compute the estimate's GRAND TOTAL exactly as the Bid Totals panel's `finalTotal`:
 * every line item's cost + all Haul & Dump + all subcontractors + per-row section
 * markup (which now includes each service's Subcontractors row), minus the discount. Every surface that shows or stores a
 * grand total must derive it here so the "Final Base Bid Total" always equals the sum
 * of the category totals shown above it.
 */
export function computeEstimateFinancials(input: EstimateFinancialsInput): EstimateFinancials {
  const { lineItems, lchRows, markupData } = input;
  const lchBySection = lchCostBySection(lchRows);

  const lineItemCost = lineItems.reduce((s, li) => s + (li.lineTotal || 0), 0);
  const hdCost = lchRows.reduce((s, r) => s + (r.totalHaulDumpCost || 0), 0);
  const subcontractors = input.subcontractors || [];
  const legacySubcontractorTotal = sumLegacySubcontractors(subcontractors);
  const subcontractorsBySection = subcontractorCostBySection(subcontractors);
  const serviceSubcontractorCost = Object.values(subcontractorsBySection).reduce((s, v) => s + v, 0);

  // Per-row markup: project sections (via the canonical section helper) + legacy
  // unsectioned rows. An absent section yields [] rows → 0 markup, so iterating the
  // fixed PROJECT_SECTIONS covers every normalized section key. Section-less H&D
  // ('_general') is counted at raw cost above and, like the panel, carries no markup.
  // Service subcontractors are marked up here through their section's row; legacy
  // ones carry their own markup inside legacySubcontractorTotal.
  let markupAmount = 0;
  for (const sec of PROJECT_SECTIONS) {
    markupAmount += computeSectionFinancials(
      sec.key, lineItems, lchBySection, markupData, subcontractorsBySection,
    ).markupAmount;
  }
  const generalRows = buildUnsectionedRows(lineItems);
  if (generalRows.length > 0) {
    const md = markupData?.['_general'] || [];
    generalRows.forEach((r, i) => {
      if (r.isTotal) return;
      markupAmount += r.subTotal * ((md[i]?.markupPercent ?? 0) / 100);
    });
  }

  const costSubtotal = lineItemCost + hdCost + legacySubcontractorTotal + serviceSubcontractorCost;
  const grandTotalBeforeDiscount = costSubtotal + markupAmount;

  const dv = input.discountValue || 0;
  const dt = input.discountType || 'percentage';
  const discountAmount = dv > 0
    ? (dt === 'percentage' ? grandTotalBeforeDiscount * (dv / 100) : dv)
    : 0;
  const total = grandTotalBeforeDiscount - discountAmount;

  return { costSubtotal, markupAmount, grandTotalBeforeDiscount, discountAmount, total };
}
